import * as React from 'react';
import { STATE } from 'app/constants';
import { FiltersPanel } from 'app/components/filters/filters-panel/filters-panel';
import { ResizeWrapper } from 'app/components/resize-wrapper';
import { Filter, FilterDomains } from 'app/components/filters/types';
import { filtersTranslations } from './constants';
import { 
  useActionCreators, 
  useFilterEventHandlers, 
  useInstanceSimpleForm,
  useLoadFiltersAndAdvancedSearchTypesEffect, 
  useSimpleFormToCompanyReportDefinitionEffect, 
  useUpdateFiltersFromCompanyReportDataEffect, 
  useUserProfile 
} from 'app/area/filters/hooks';
import { useTranslation } from 'app/infrastructure/translate/hooks';
import { useInstanceKey } from 'app/area/favourite/favourite-context';
import { useSelector } from 'react-redux';
import { selectAdvancedSearchTypes, selectFilterDomains, selectReportData } from 'app/area/filters/selectors';
import { FavouriteDefinition, TextCollection } from 'app/types-shared';
import { reportTypes } from 'app/area/favourite/constants';
import { useMemo } from 'react';

export interface ReportComponentProps {
  subtitle: string;
  validateFilters?: boolean;
  companyReportId?: number;
  predefinedTitle?: string;
  definition: FavouriteDefinition;
  currentList: Filter[];
  locale: string;
}

export function FiltersCompanyReport({
  subtitle,
  validateFilters,
  companyReportId,
  predefinedTitle,
  definition,
  currentList,
  locale
}: ReportComponentProps) {
  const instanceKey = useInstanceKey();
  const texts: TextCollection = useSelector(state => state[STATE.filters]?.texts);
  
  const {
    simpleForm,
    validation,
    validate,
    SimpleField
  } = useInstanceSimpleForm({ texts });

  const userProfile = useUserProfile();

  const translation = useTranslation(STATE.filters, filtersTranslations);

  const advancedSearchTypes = useSelector(selectAdvancedSearchTypes(instanceKey));
  const reportData = useSelector(selectReportData(instanceKey));
  const filterDomains = useSelector(selectFilterDomains(instanceKey));

  const memoizedEditingValues = useMemo(() => simpleForm?.editing?.values, [JSON.stringify(simpleForm?.editing?.values)]);

  const {
    onFilterMenuOption,
    onRemoveFilters
  } = useFilterEventHandlers(simpleForm, currentList, advancedSearchTypes);

  const {
    setFilterOption
  } = useActionCreators();
  
  useLoadFiltersAndAdvancedSearchTypesEffect(reportTypes.specialReport, companyReportId);

  useUpdateFiltersFromCompanyReportDataEffect(
    definition, filterDomains, advancedSearchTypes, reportData, predefinedTitle, translation
  );

  useSimpleFormToCompanyReportDefinitionEffect(
    memoizedEditingValues, currentList
  );

  const currentListFilteredOnUsingPredefDate = reportData && !reportData.usePredefDate ?
    currentList.filter(cl => cl.domain !== FilterDomains.PredefDate) : currentList;
  
  return (    
    <div className="page-heading">
      {!!currentListFilteredOnUsingPredefDate && currentListFilteredOnUsingPredefDate.length > 0 && 
        <div>
          <h3 className="tertiary-group-title">{subtitle}</h3>
          <ResizeWrapper>
            <FiltersPanel
              filtersDefinition={currentListFilteredOnUsingPredefDate}
              editingValues={simpleForm.editing.values}
              translation={translation}
              userProfile={userProfile}
              onFilterMenuOption={onFilterMenuOption}
              onRemove={onRemoveFilters}
              validate={validate}
              validation={validation}
              locale={locale}
              onFilterOptionType={setFilterOption}
              validateFilters={validateFilters}
              SimpleField={SimpleField}
            />
          </ResizeWrapper>
        </div>
      }
    </div>
  );
}
